import { useLayoutEffect, useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

import { FormControl, Label } from 'components/UIComponents';
import { InputFieldContainer } from 'components/UIComponents/input/Select/styledComponents';

import { colors } from 'styles/globalStyles';

const TOOLTIP_MAX_WIDTH = 360;

interface OriginSpotsFieldProps {
  label: string;
  value: string;
  disabled: boolean;
  onClick: () => void;
  helperText?: string;
  error?: boolean;
}

const FieldButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean; disabled?: boolean }>(({ error, disabled }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '6px 8px',
  height: 32,
  borderRadius: 4,
  border: `1px solid ${error ? colors.red : colors.borderGrey}`,
  backgroundColor: disabled ? '#f5f5f5' : colors.white,
  color: disabled ? '#9e9e9e' : '#212529',
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: '20px',
  textAlign: 'left',
  cursor: disabled ? 'not-allowed' : 'pointer',
  overflow: 'hidden',
  '&:focus': {
    outline: 'none',
    borderColor: error ? colors.red : colors.focusBlue,
  },
}));

const ValueText = styled('span')({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Placeholder = styled(ValueText)({
  color: '#bdbdbd',
});

const OriginSpotsField = ({
  label,
  value,
  disabled,
  onClick,
  helperText,
  error = false,
}: OriginSpotsFieldProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useLayoutEffect(() => {
    if (textRef.current) {
      setIsOverflowed(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [value]);

  return (
    <FormControl>
      <Label disabled={disabled}>{label}</Label>
      <InputFieldContainer>
        <FieldButton
          type="button"
          error={error}
          disabled={disabled}
          onClick={() => {
            if (!disabled) onClick();
          }}
          aria-label={label}
        >
          {value ? (
            <Tooltip
              title={value}
              placement="top"
              disableHoverListener={!isOverflowed}
              disableFocusListener={!isOverflowed}
              disableTouchListener={!isOverflowed}
              enterDelay={150}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: TOOLTIP_MAX_WIDTH,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    fontSize: 13,
                    lineHeight: 1.45,
                    padding: '8px 10px',
                  },
                },
              }}
            >
              <ValueText ref={textRef}>{value}</ValueText>
            </Tooltip>
          ) : (
            <Placeholder>&nbsp;</Placeholder>
          )}
          <ArrowDropDownIcon sx={{ color: disabled ? '#bdbdbd' : '#757575' }} />
        </FieldButton>
        {helperText && <Label error={error}>{helperText}</Label>}
      </InputFieldContainer>
    </FormControl>
  );
};

export default OriginSpotsField;
